import React from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';

import {
  UiNode,
  UiNodeInputAttributes,
} from '@ory/kratos-client';


type SetNodeValue = (key: string, value: any) => void;


/*
React Component wrapping a Kratos UiNode and its 
synced React-controlled value.

Helper to kratos/Form Component.
*/
export default function FormNode(props: FormNodeProps) {
  ensureInputNode(props.node);
  return FormInputNode(props);
}


interface FormNodeProps {
  node: UiNode;
  value: any;
  valueSetter: SetNodeValue;
}


function FormInputNode(props: FormNodeProps) {
  const node = props.node;
  const attributes = node.attributes as UiNodeInputAttributes;
  const key = getNodeKey(props.node);

  let val = props.value;
  if (val === undefined) {
    // undefined values cause React to assume 'uncontrolled',
    // so this helps keep things smoothly in fully-controlled mode.
    val = '';
  }

  const uiType = attributes.type;
  const label = node.meta.label?.text;

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const newValue = e.target.value;
    props.valueSetter(key, newValue);
  }


  if (uiType === 'submit') {
    return (
      <Button
        key={key}
        type="submit"
        value={val}
        variant="contained"
        size="large"
      >
        {label}
      </Button>);
  }

  if (uiType === 'text' || uiType === 'password' || uiType === 'email') {
    const nodeUi = (
      <TextField
        value={val}
        onChange={handleChange}
        type={uiType}
        required={attributes.required}
        label={label}
        inputProps={{pattern: attributes.pattern}}
        autoComplete={uiType === 'password' ? 'off' : 'on'}
        margin="normal" />);

    const messages = node.messages.map(message => 
      <Alert key={message.text} severity="error">
        {message.text}
      </Alert>);

    return (
      <Stack key={key}>
        {nodeUi}
        {messages}
      </Stack>);
  }

  // fall-back for unimplemented types of UiNodeInputAttributesTypeEnum:
  // for hidden this works fine, but we may need to add other node types
  // as we add more flows (TODO).

  return (
    <div key={key}>
      <input  {...attributes} value={val} onChange={handleChange} />
      { label ? <span>{label}</span> : null }
    </div>
  );
}


/*
TODO: implement other types that Kratos can return, if start
implementing other flows that use them:
UiNodeTypeEnum also has: Text,Img,Script,A
When do so, make sure to adjust everywhere that calls this, and then
remove it.
*/
function ensureInputNode(node: UiNode) {
  if (node.type !== 'input') {
    throw Error('Unimplemented node type.');
  }
  return true;
}


/*
Some helper functions for managing UiNode values..
The values are "controlled" by React, but initial/refresh values
may come from Kratos flow (re)fetches.
An additional complication is that undefined values can mess with
React, since this signals "uncontrolled", but Kratos uses undefined
to mark non-input optional values.
Note: there may be multiple submit inputs with same group & name,
eg in group 'oidc' when there are multiple OIDC providers, so we
need another element in the key to disambiguate... luckily the
values themselves should be static in this special case, so can
use them.
*/
export function getNodeKey(node: UiNode) {
  ensureInputNode(node);
  const attributes = node.attributes as UiNodeInputAttributes;
  let key = `${node.group}-${attributes.name}`;
  if (attributes.type == 'submit') {
    key = `${key}-${attributes.value}`;
  }
  return key;
}


export function getNodeInitialValue(node: UiNode): any {
  ensureInputNode(node);
  const attributes = node.attributes as UiNodeInputAttributes;
  const value = attributes.value;
  // prevent undefined values to prevent React treating inputs
  // using them as "uncontrolled":
  if (value === undefined) {
    return '';
  }
  return value;
}
